// =============================================================================
// DEV-193: TENANT ISOLATION CONFIGURATION
// =============================================================================

import { TenantType } from './tenant-base.model';

/**
 * Isolation level determines how tenant resources are separated
 */
export type IsolationLevel =
  | 'shared'     // All tenants share same infrastructure
  | 'logical'    // Logical separation via namespace/schema
  | 'physical'   // Physically separate databases/storage
  | 'dedicated'; // Dedicated infrastructure per tenant

/**
 * Data isolation configuration
 */
export interface DataIsolationConfig {
  /** Overall data isolation level */
  level: IsolationLevel;
  /** Separate database schema per tenant */
  schemaIsolation: boolean;
  /** Encrypt data at rest with tenant-specific keys */
  encryptionAtRest: boolean;
  /** Encrypt data in transit */
  encryptionInTransit: boolean;
  /** Tenant-specific encryption key ID */
  encryptionKeyId?: string;
  /** Data backup isolation */
  backupIsolation: boolean;
  /** Separate backup schedule per tenant */
  separateBackupSchedule: boolean;
  /** Data retention days */
  dataRetentionDays: number;
  /** Enable cross-tenant data access (admin only) */
  allowCrossTenantAccess: boolean;
  /** Data residency region */
  dataResidency?: string;
  /** GDPR/compliance data isolation */
  complianceIsolation: boolean;
}

/**
 * Network isolation configuration
 */
export interface NetworkIsolationConfig {
  /** Overall network isolation level */
  level: IsolationLevel;
  /** Private VLAN/subnet per tenant */
  vlanIsolation: boolean;
  /** VLAN ID if isolated */
  vlanId?: string;
  /** Dedicated IP ranges */
  ipRanges?: string[];
  /** Firewall rules isolation */
  firewallIsolation: boolean;
  /** Custom firewall rules */
  firewallRules?: FirewallRule[];
  /** Enable VPN access */
  vpnAccess: boolean;
  /** VPN configuration */
  vpnConfig?: VpnConfig;
  /** API gateway isolation */
  apiGatewayIsolation: boolean;
  /** Custom domain per tenant */
  customDomain?: string;
  /** SSL certificate ID */
  sslCertificateId?: string;
  /** DDoS protection level */
  ddosProtection: 'basic' | 'standard' | 'advanced';
  /** Rate limiting at network level */
  networkRateLimit?: number;
}

/**
 * Firewall rule definition
 */
export interface FirewallRule {
  id: string;
  name: string;
  direction: 'inbound' | 'outbound';
  protocol: 'tcp' | 'udp' | 'icmp' | 'all';
  sourceIp: string;
  destinationIp: string;
  sourcePort?: string;
  destinationPort?: string;
  action: 'allow' | 'deny';
  priority: number;
  enabled: boolean;
}

/**
 * VPN configuration
 */
export interface VpnConfig {
  type: 'ipsec' | 'wireguard' | 'openvpn';
  endpoint: string;
  publicKey?: string;
  presharedKey?: string;
  tunnelCidr: string;
  enabled: boolean;
}

/**
 * Compute isolation configuration
 */
export interface ComputeIsolationConfig {
  /** Overall compute isolation level */
  level: IsolationLevel;
  /** Dedicated compute nodes */
  dedicatedNodes: boolean;
  /** Number of dedicated nodes */
  nodeCount?: number;
  /** Node pool name/ID */
  nodePoolId?: string;
  /** CPU resource isolation (cgroups) */
  cpuIsolation: boolean;
  /** Guaranteed CPU cores */
  guaranteedCpuCores?: number;
  /** Maximum CPU cores */
  maxCpuCores?: number;
  /** Memory isolation */
  memoryIsolation: boolean;
  /** Guaranteed memory GB */
  guaranteedMemoryGb?: number;
  /** Maximum memory GB */
  maxMemoryGb?: number;
  /** Process isolation (containers/VMs) */
  processIsolation: 'container' | 'vm' | 'bare-metal';
  /** GPU isolation */
  gpuIsolation: boolean;
  /** GPU type and count */
  gpuConfig?: {
    type: string;
    count: number;
  };
  /** Workload scheduling priority */
  schedulingPriority: 'low' | 'normal' | 'high' | 'critical';
}

/**
 * Storage isolation configuration
 */
export interface StorageIsolationConfig {
  /** Overall storage isolation level */
  level: IsolationLevel;
  /** Separate storage volumes */
  volumeIsolation: boolean;
  /** Storage volume ID */
  volumeId?: string;
  /** Storage class/tier */
  storageClass: 'standard' | 'premium' | 'archive';
  /** IOPS guarantee */
  guaranteedIops?: number;
  /** Maximum IOPS */
  maxIops?: number;
  /** Throughput guarantee MB/s */
  guaranteedThroughputMbps?: number;
  /** File system encryption */
  filesystemEncryption: boolean;
  /** Encryption algorithm */
  encryptionAlgorithm?: 'aes-256-gcm' | 'chacha20-poly1305';
  /** Snapshot isolation */
  snapshotIsolation: boolean;
  /** Object storage bucket isolation */
  objectStorageIsolation: boolean;
  /** Object storage bucket name */
  objectStorageBucket?: string;
}

/**
 * Logging and audit isolation configuration
 */
export interface LoggingIsolationConfig {
  /** Separate log streams */
  logStreamIsolation: boolean;
  /** Log stream/index name */
  logStreamName?: string;
  /** Audit log isolation */
  auditLogIsolation: boolean;
  /** Audit log retention days */
  auditRetentionDays: number;
  /** Log encryption */
  logEncryption: boolean;
  /** Log access control */
  logAccessControl: 'shared' | 'tenant-only' | 'admin-only';
  /** Real-time log streaming */
  realTimeStreaming: boolean;
  /** External log forwarding */
  logForwarding?: {
    enabled: boolean;
    destination: string;
    format: 'json' | 'syslog' | 'cef';
  };
}

/**
 * Complete tenant isolation configuration (DEV-193)
 */
export interface TenantIsolationConfig {
  /** Tenant ID */
  tenantId: string;
  /** Overall isolation level (determines defaults) */
  isolationLevel: IsolationLevel;
  /** Data isolation settings */
  data: DataIsolationConfig;
  /** Network isolation settings */
  network: NetworkIsolationConfig;
  /** Compute isolation settings */
  compute: ComputeIsolationConfig;
  /** Storage isolation settings */
  storage: StorageIsolationConfig;
  /** Logging isolation settings */
  logging: LoggingIsolationConfig;
  /** Configuration metadata */
  effectiveDate: Date;
  /** Last update timestamp */
  updatedAt: Date;
  /** Updated by user */
  updatedBy: string;
  /** Change history */
  changeHistory?: IsolationChangeRecord[];
}

/**
 * Isolation change record for audit
 */
export interface IsolationChangeRecord {
  id: string;
  timestamp: Date;
  changedBy: string;
  section: 'data' | 'network' | 'compute' | 'storage' | 'logging' | 'overall';
  previousLevel?: IsolationLevel;
  newLevel?: IsolationLevel;
  changes: Record<string, { old: unknown; new: unknown }>;
  reason?: string;
  approvedBy?: string;
}

/**
 * Isolation level metadata for UI
 */
export interface IsolationLevelInfo {
  level: IsolationLevel;
  label: string;
  description: string;
  icon: string;
  color: string;
  features: string[];
  recommendedFor: TenantType[];
}

/**
 * Isolation level metadata lookup
 */
export const ISOLATION_LEVEL_INFO: Record<IsolationLevel, IsolationLevelInfo> = {
  shared: {
    level: 'shared',
    label: 'Shared',
    description: 'All tenants share same infrastructure with logical access controls',
    icon: 'pi pi-users',
    color: '#3b82f6', // blue
    features: [
      'Cost-effective',
      'Access control via ACLs',
      'Shared compute pool',
      'Common storage backend'
    ],
    recommendedFor: ['trial', 'standard']
  },
  logical: {
    level: 'logical',
    label: 'Logical',
    description: 'Logical separation with dedicated schemas/namespaces',
    icon: 'pi pi-box',
    color: '#8b5cf6', // purple
    features: [
      'Separate database schemas',
      'Namespace isolation',
      'Dedicated queues',
      'Resource quotas'
    ],
    recommendedFor: ['standard', 'professional']
  },
  physical: {
    level: 'physical',
    label: 'Physical',
    description: 'Physically separate databases and storage volumes',
    icon: 'pi pi-server',
    color: '#f59e0b', // amber
    features: [
      'Dedicated databases',
      'Separate storage volumes',
      'Network segmentation',
      'Independent scaling'
    ],
    recommendedFor: ['professional', 'enterprise']
  },
  dedicated: {
    level: 'dedicated',
    label: 'Dedicated',
    description: 'Fully dedicated infrastructure per tenant',
    icon: 'pi pi-building',
    color: '#10b981', // emerald
    features: [
      'Dedicated compute nodes',
      'Private networking',
      'Custom security policies',
      'Full resource control'
    ],
    recommendedFor: ['enterprise']
  }
};

/**
 * Create default isolation configuration based on tenant type
 */
export function createDefaultIsolationConfig(
  tenantId: string,
  tenantType: TenantType,
  updatedBy: string
): TenantIsolationConfig {
  // Determine default isolation level based on tenant type
  const levelMap: Record<TenantType, IsolationLevel> = {
    enterprise: 'dedicated',
    professional: 'physical',
    standard: 'logical',
    trial: 'shared',
    internal: 'logical'
  };

  const level = levelMap[tenantType];
  const isHighIsolation = level === 'physical' || level === 'dedicated';

  return {
    tenantId,
    isolationLevel: level,
    data: {
      level,
      schemaIsolation: level !== 'shared',
      encryptionAtRest: true,
      encryptionInTransit: true,
      encryptionKeyId: isHighIsolation ? `key-${tenantId}` : undefined,
      backupIsolation: isHighIsolation,
      separateBackupSchedule: level === 'dedicated',
      dataRetentionDays: tenantType === 'enterprise' ? 365 : tenantType === 'professional' ? 180 : 90,
      allowCrossTenantAccess: false,
      complianceIsolation: isHighIsolation
    },
    network: {
      level,
      vlanIsolation: isHighIsolation,
      firewallIsolation: level !== 'shared',
      vpnAccess: level === 'dedicated',
      apiGatewayIsolation: level !== 'shared',
      ddosProtection: tenantType === 'enterprise' ? 'advanced' : tenantType === 'professional' ? 'standard' : 'basic'
    },
    compute: {
      level,
      dedicatedNodes: level === 'dedicated',
      cpuIsolation: level !== 'shared',
      memoryIsolation: level !== 'shared',
      processIsolation: level === 'dedicated' ? 'vm' : 'container',
      gpuIsolation: false,
      schedulingPriority: tenantType === 'enterprise' ? 'critical' : tenantType === 'professional' ? 'high' : 'normal'
    },
    storage: {
      level,
      volumeIsolation: isHighIsolation,
      storageClass: tenantType === 'enterprise' ? 'premium' : 'standard',
      filesystemEncryption: true,
      encryptionAlgorithm: 'aes-256-gcm',
      snapshotIsolation: level !== 'shared',
      objectStorageIsolation: isHighIsolation
    },
    logging: {
      logStreamIsolation: level !== 'shared',
      auditLogIsolation: true,
      auditRetentionDays: tenantType === 'enterprise' ? 730 : tenantType === 'professional' ? 365 : 180,
      logEncryption: true,
      logAccessControl: level === 'shared' ? 'tenant-only' : 'tenant-only',
      realTimeStreaming: tenantType !== 'trial'
    },
    effectiveDate: new Date(),
    updatedAt: new Date(),
    updatedBy
  };
}

/**
 * Get recommended isolation level for tenant type
 */
export function getRecommendedIsolationLevel(tenantType: TenantType): IsolationLevel {
  const recommendations: Record<TenantType, IsolationLevel> = {
    enterprise: 'dedicated',
    professional: 'physical',
    standard: 'logical',
    trial: 'shared',
    internal: 'logical'
  };
  return recommendations[tenantType];
}

/**
 * Check if isolation level upgrade is allowed
 */
export function canUpgradeIsolation(
  currentLevel: IsolationLevel,
  targetLevel: IsolationLevel
): boolean {
  const levelOrder: IsolationLevel[] = ['shared', 'logical', 'physical', 'dedicated'];
  return levelOrder.indexOf(targetLevel) >= levelOrder.indexOf(currentLevel);
}

/**
 * Calculate isolation score (0-100) based on configuration
 */
export function calculateIsolationScore(config: TenantIsolationConfig): number {
  let score = 0;
  const maxScore = 100;

  // Base score from level (40 points max)
  const levelScores: Record<IsolationLevel, number> = {
    shared: 10,
    logical: 20,
    physical: 30,
    dedicated: 40
  };
  score += levelScores[config.isolationLevel];

  // Data isolation (20 points max)
  if (config.data.schemaIsolation) {
    score += 5;
  }
  if (config.data.encryptionAtRest) {
    score += 5;
  }
  if (config.data.encryptionInTransit) {
    score += 5;
  }
  if (config.data.backupIsolation) {
    score += 5;
  }

  // Network isolation (20 points max)
  if (config.network.vlanIsolation) {
    score += 5;
  }
  if (config.network.firewallIsolation) {
    score += 5;
  }
  if (config.network.vpnAccess) {
    score += 5;
  }
  if (config.network.ddosProtection === 'advanced') {
    score += 5;
  } else if (config.network.ddosProtection === 'standard') {
    score += 3;
  }

  // Compute isolation (10 points max)
  if (config.compute.dedicatedNodes) {
    score += 5;
  }
  if (config.compute.cpuIsolation && config.compute.memoryIsolation) {
    score += 5;
  }

  // Logging isolation (10 points max)
  if (config.logging.auditLogIsolation) {
    score += 5;
  }
  if (config.logging.logEncryption) {
    score += 5;
  }

  return Math.min(score, maxScore);
}
